import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface AdminUserEditDialogProps {
  user: any;

  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdminUserEditDialog: React.FC<AdminUserEditDialogProps> = ({ open, onClose, onSuccess, user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  useEffect(() => {
    if (open && user) {
      setFormData({
        full_name: user?.name || user?.profile?.full_name || '',
        furigana: user?.profile?.furigana || '',
        gender: user?.profile?.gender || '',
        birth_date: user?.profile?.birth_date ? user.profile.birth_date.split('T')[0] : '',
        email: user?.email || '',
        phone_number: user?.phone_number || '',
        chart_number: user?.chart_number || '',
        password: '',
        postal_code: user?.profile?.postal_code || '',
        address: user?.profile?.address || ''
      });
    }
  }, [open, user]);

  const [formData, setFormData] = useState({
    full_name: '',
    furigana: '',
    gender: 'other',
    birth_date: '',
    email: '',
    phone_number: '',
    chart_number: '',
    password: '',
    postal_code: '',
    address: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Clean up empty strings to avoid Pydantic validation errors for Optional fields
    const submissionData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key, 
        value === '' ? (['email', 'phone_number', 'chart_number', 'password', 'furigana', 'gender', 'birth_date', 'postal_code', 'address'].includes(key) ? null : value) : value
      ])
    );

    try {
      await api.put(`/admin/users/${user.id}/manual`, submissionData);
      toast.success(t('admin.save_success'));
      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('common.save_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      full_name: '',
      furigana: '',
      gender: 'other',
      birth_date: '',
      email: '',
      phone_number: '',
      chart_number: '',
      password: '',
      postal_code: '',
      address: ''
    });
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[50] p-4" onClick={handleClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50 shrink-0">
          <h3 className="text-xl font-bold text-gray-800">{t('admin.edit_user_title')}</h3>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm border border-red-200">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.full_name_label')} *</label>
              <input
                required
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.furigana_label')}</label>
              <input
                type="text"
                name="furigana"
                value={formData.furigana}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.gender_label')}</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none h-[38px]"
              >
                <option value="male">{t('admin.gender_male')}</option>
                <option value="female">{t('admin.gender_female')}</option>
                <option value="other">{t('admin.gender_other')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.birth_date_label')}</label>
              <input
                type="date"
                name="birth_date"
                value={formData.birth_date}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.phone_number_label')}</label>
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.chart_number_label')}</label>
              <input
                type="text"
                name="chart_number"
                placeholder="1000000"
                value={formData.chart_number}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none"
              />
            </div>
            
          </div>


            <div className="md:col-span-2 grid grid-cols-2 gap-4 border-t pt-4 mt-2">
              <h4 className="col-span-2 text-sm font-bold text-gray-700">{t('profile.address') || 'Address'}</h4>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('profile.postal_code') || 'Postal Code'}</label>
                <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('profile.address') || 'Address'}</label>
                <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-coral outline-none" />
              </div>
            </div>

          <div className="mt-2 text-xs text-gray-500 italic">
            * {t('admin.chart_number_label')} ({t('admin.auto_allocate_hint')})
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              {t('admin.cancel_label') || '取消'}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 text-sm font-semibold text-white rounded-lg transition shadow-md ${
                loading ? 'bg-coral/80 cursor-not-allowed' : 'bg-coral hover:opacity-90 active:scale-95'
              }`}
            >
              {loading ? (t('admin.loading') || '...') : (t('admin.save') || '添加用户')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminUserEditDialog;
